import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import moment from "moment";

import { getMockUser, isImpersonating } from "../../../helpers/impersonation";
import { removeToken } from "../../../helpers/localStorageCache";
import { LOCALSTORAGE_KEYS } from "../../../constants/users";
import { dropUnscopedStoreWizardDraft } from "../../../helpers/storeWizardDraft";
import { dropUnscopedBulkCreateDrafts } from "../../../helpers/bulkCreateDraftStorage";

/**
 * Shown while an admin is impersonating a seller whose account is on hold.
 * Release exits access and opens the Users page release dialog — API calls
 * while mock-uid is active run as the seller and cannot toggle hold.
 */
const AdminHoldBanner = () => {
  const user = useSelector((state) => state?.currentUser?.user);
  const enterprise = useSelector((state) => state?.currentUser?.enterprise);
  const mockUser = getMockUser();
  const isMockUser = isImpersonating(mockUser);
  const visible = isMockUser && !!user?.ordersOnHold;

  useEffect(() => {
    if (!visible) return undefined;
    document.body.classList.add("has-riverr-global-banner");
    return () => document.body.classList.remove("has-riverr-global-banner");
  }, [visible]);

  if (!visible) return null;

  const business =
    enterprise?.name ||
    user?.businessName ||
    user?.displayName ||
    user?.email ||
    "this account";

  let heldSince = null;
  const raw = user?.ordersOnHoldAt;
  const epoch =
    typeof raw === "number"
      ? raw
      : raw?._seconds ?? raw?.seconds ?? null;
  if (epoch) heldSince = moment.unix(epoch).format("MMM D");

  const handleRelease = () => {
    const uid = user?.uid || mockUser?.uid;
    dropUnscopedStoreWizardDraft();
    dropUnscopedBulkCreateDrafts();
    removeToken(LOCALSTORAGE_KEYS.mockUser);
    window.location.href = uid
      ? `/users?releaseHold=${encodeURIComponent(uid)}`
      : "/users";
  };

  return (
    <Alert
      severity="warning"
      icon={<WarningAmberRoundedIcon fontSize="inherit" />}
      data-riverr-global-banner
      sx={{ mt: "60px", borderRadius: 0, alignItems: "flex-start" }}
      action={
        <Button
          color="inherit"
          size="small"
          variant="outlined"
          onClick={handleRelease}
          sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
        >
          Release hold
        </Button>
      }
    >
      <AlertTitle sx={{ fontWeight: 700 }}>
        {business} is on hold{heldSince ? ` · since ${heldSince}` : ""}
      </AlertTitle>
      Orders are coming in but not entering production. The seller sees this
      same message on their end. Release exits access and opens the confirm
      dialog on Users.
    </Alert>
  );
};

export default AdminHoldBanner;
